/**
 * Reine Praesentations-Helper fuer Domain-Rows: Status-Label/Variant + Restlaufzeit
 * fuer Vertraege und Faelligkeits-Label fuer Lieferanten-Rechnungen.
 *
 * Halte Variant-Namen synchron mit Pill (default/accent/warning/error/outline).
 */
import type { ContractResponse, SupplierInvoiceResponse } from '@/shared/api';

type PillVariant = 'default' | 'accent' | 'warning' | 'error' | 'outline';

export interface ContractStatusDisplay {
  label: string;
  variant: PillVariant;
}

export function contractStatusDisplay(c: ContractResponse): ContractStatusDisplay {
  if (c.status === 'Cancelled') return { label: 'Gekündigt', variant: 'error' };
  if (c.status === 'Archived') return { label: 'Archiviert', variant: 'default' };
  return { label: 'Aktiv', variant: 'accent' };
}

/** "läuft (auto)", "läuft (offen)", "endet in 8 Mon", "endet am 31.12.2026", "abgelaufen". */
export function contractRuntimeLabel(c: ContractResponse, today = new Date()): string {
  if (c.status === 'Cancelled') return 'gekündigt';
  if (c.status === 'Archived') return 'archiviert';
  if (!c.endDate) return c.autoRenewal ? 'läuft (auto-renewal)' : 'läuft (offen)';
  const end = new Date(c.endDate);
  const diffDays = Math.round((end.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return 'abgelaufen';
  if (diffDays <= 30) return `endet in ${diffDays} Tagen`;
  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `endet in ${diffMonths} Mon`;
  return `endet am ${formatDeDate(c.endDate)}`;
}

export function isContractExpiringSoon(c: ContractResponse, today = new Date()): boolean {
  if (!c.endDate || c.status !== 'Active') return false;
  const end = new Date(c.endDate);
  const diffDays = (end.getTime() - today.getTime()) / 86400000;
  return diffDays >= 0 && diffDays <= 90;
}

export interface InvoiceStatusDisplay {
  label: string;
  variant: PillVariant;
}

export function invoiceStatusDisplay(inv: SupplierInvoiceResponse): InvoiceStatusDisplay {
  switch (inv.status) {
    case 'Paid':
      return { label: 'Bezahlt', variant: 'accent' };
    case 'Overdue':
      return { label: 'Überfällig', variant: 'error' };
    case 'Disputed':
      return { label: 'Strittig', variant: 'warning' };
    default:
      return { label: 'Offen', variant: 'default' };
  }
}

export function invoiceDueLabel(inv: SupplierInvoiceResponse, today = new Date()): string {
  if (inv.paidOn) return `bezahlt ${formatDeDate(inv.paidOn)}`;
  if (!inv.dueOn) return 'kein Fälligkeitsdatum';
  const due = new Date(inv.dueOn);
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return `${Math.abs(diffDays)} Tage überfällig`;
  if (diffDays === 0) return 'heute fällig';
  if (diffDays <= 14) return `fällig in ${diffDays} Tagen`;
  return `fällig ${formatDeDate(inv.dueOn)}`;
}

function formatDeDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}
